import { StyledSelect } from "../../primitives/styled-select";
import type * as React from "react";
import { ArrowDownLeft, ArrowUpRight } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type {
  ActionFieldError,
  ActionFieldSpec,
  ActionFieldValue,
} from "../ActionFieldEditor";
import {
  ENVELOPE_CLASSES,
  asString,
  specOfName,
  useEnvelopeCommit,
} from "./envelope-primitives";
import {
  VendorPreviewField,
  VendorPreviewShell,
} from "./VendorPreviewShell";
import { fmtCurrencyEUR, pickField } from "./previewUtils";
import {
  DateTimePicker,
  ymdToDate,
  dateToYmd,
} from "../../primitives/date-time-picker";

/**
 * FicPaymentPreview — controlled HITL editor for the Fatture in Cloud
 * "Registra incasso / pagamento" sub-action (``mark_invoice_paid``).
 *
 * Mirrors the controlled render-prop contract of :class:`SepaPaymentPreview`
 * / :class:`EmailSendDraftPreview`: the parent (``ActionDrawer`` via
 * ``pickPreview``) hands ``schema`` / ``values`` / ``errors`` / ``onChange``
 * / ``disabled`` so the editable inputs bind to the drawer's LIVE values, and
 * the stacked ``ActionFieldEditor`` skips the fields in
 * :data:`FIC_PAYMENT_PREVIEW_OWNED_FIELDS`. Without the controlled API it
 * degrades to a read-only snapshot (post-execution / ``executed`` / ``failed``).
 *
 * Layout (top → bottom):
 *   1. Amount hero — the invoice total being settled (read-only context;
 *      ``amount`` is NOT a schema field, it rides the proposal for display).
 *   2. Invoice / customer context (read-only).
 *   3. Editable: ``payment_date`` (required), ``payment_account_id``
 *      (SELECT when the connection exposes bank accounts, number otherwise),
 *      ``notes``.
 *
 * Field keys match the FiC connector schema for ``mark_invoice_paid``:
 * ``record_id`` (readonly) / ``payment_date`` / ``payment_account_id`` /
 * ``notes``.
 */

export const FIC_PAYMENT_PREVIEW_OWNED_FIELDS = [
  "record_id",
  "payment_date",
  "payment_account_id",
  "notes",
] as const;

type FicPaymentFieldName = (typeof FIC_PAYMENT_PREVIEW_OWNED_FIELDS)[number];

export interface FicPaymentPreviewProps {
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
  /** Controlled API — present when the proposal is still editable. */
  schema?: readonly ActionFieldSpec[];
  values?: Readonly<Record<string, ActionFieldValue>>;
  errors?: Readonly<Record<string, ActionFieldError | undefined>>;
  onChange?: (next: Record<string, ActionFieldValue>) => void;
  disabled?: boolean;
  className?: string;
}

export const FicPaymentPreview: React.FC<FicPaymentPreviewProps> = ({
  proposedFields,
  executedFields,
  schema,
  values,
  errors,
  onChange,
  disabled,
  className,
}) => {
  const { i18n } = useLingui();
  const isControlled = Boolean(values && onChange);
  const liveValues = values ?? executedFields ?? proposedFields;

  // ── Read-only display context (NOT schema fields; ride for display) ──────
  const amount = executedFields?.amount ?? proposedFields.amount;
  const invoiceNumber =
    pickField(proposedFields, executedFields, "invoice_number") ||
    pickField(proposedFields, executedFields, "reference");
  const customer =
    pickField(proposedFields, executedFields, "customer_name") ||
    pickField(proposedFields, executedFields, "client_name");
  const direction = pickField(proposedFields, executedFields, "direction");
  const isOutgoing = direction === "outgoing" || direction === "expense";
  const DirectionIcon = isOutgoing ? ArrowUpRight : ArrowDownLeft;
  const directionLabel = isOutgoing
    ? i18n._(msg`Pagamento (uscita)`)
    : i18n._(msg`Incasso (entrata)`);

  // ── Editable schema fields ───────────────────────────────────────────────
  const dateSpec = specOfName(schema, "payment_date");
  const accountSpec = specOfName(schema, "payment_account_id");
  const notesSpec = specOfName(schema, "notes");

  const commit = useEnvelopeCommit<FicPaymentFieldName>(values, onChange);

  const dateValue = asString(liveValues.payment_date);
  const accountValue = asString(liveValues.payment_account_id);
  const notesValue = asString(liveValues.notes);
  const accountOptions = accountSpec?.options ?? [];
  const accountLabel =
    accountOptions.find((o) => o.value === accountValue)?.label || accountValue;

  // Invoice / customer context — rendered as the sheet caption.
  const contextCaption =
    invoiceNumber || customer ? (
      <span className="text-ink-2">
        {invoiceNumber ? (
          <span className="font-medium">
            {i18n._(msg`Fattura n. ${invoiceNumber}`)}
          </span>
        ) : null}
        {invoiceNumber && customer ? " · " : null}
        {customer || null}
      </span>
    ) : undefined;

  // Amount rides for display only (NOT a schema field) — promoted to the
  // emphasis band: amber (warning) for an outgoing payment, orange (accent)
  // for an incoming collection, mirroring the old amber hero.
  const amountBanner = {
    label: i18n._(msg`Importo`),
    value: <span className="tabular-nums">{fmtCurrencyEUR(amount)}</span>,
    tone: (isOutgoing ? "warning" : "accent") as "warning" | "accent",
  };

  // -------------------------------------------------------------------------
  // Read-only fallback — post-execution snapshot (no editable inputs).
  // -------------------------------------------------------------------------
  if (!isControlled) {
    return (
      <VendorPreviewShell
        className={className}
        icon={<DirectionIcon className="h-4 w-4" aria-hidden />}
        title={directionLabel}
        caption={contextCaption}
        bottomBanner={amountBanner}
      >
        <VendorPreviewField
          label={i18n._(msg`Data pagamento`)}
          value={
            dateValue ? <span className="tabular-nums">{dateValue}</span> : null
          }
          hideEmpty
        />
        <VendorPreviewField
          label={i18n._(msg`Conto`)}
          value={accountLabel || null}
          hideEmpty
        />
        <VendorPreviewField
          label={i18n._(msg`Note`)}
          wide
          value={notesValue || null}
          hideEmpty
        />
      </VendorPreviewShell>
    );
  }

  // -------------------------------------------------------------------------
  // Controlled — register-payment editor.
  // -------------------------------------------------------------------------
  return (
    <VendorPreviewShell
      className={className}
      icon={<DirectionIcon className="h-4 w-4" aria-hidden />}
      title={
        isOutgoing
          ? i18n._(msg`Registra pagamento`)
          : i18n._(msg`Registra incasso`)
      }
      caption={contextCaption}
      bottomBanner={amountBanner}
      footnote={i18n._(
        msg`L'operazione verrà registrata su Fatture in Cloud con audit; al cliente può essere inviata un'email di conferma opzionale.`,
      )}
    >
      {/* (3a) Data pagamento — required. */}
      <VendorPreviewField
        label={i18n._(msg`Data`)}
        value={
          <div className="flex flex-col gap-1">
            <DateTimePicker
              value={ymdToDate(dateValue)}
              onChange={(d) => commit("payment_date", d ? dateToYmd(d) : "")}
              invalid={Boolean(errors?.payment_date)}
              disabled={disabled || dateSpec?.readonly}
              ariaLabel={i18n._(msg`Data del pagamento`)}
            />
            {errors?.payment_date ? (
              <p className={ENVELOPE_CLASSES.err}>
                {errors.payment_date.message}
              </p>
            ) : (
              <p className={ENVELOPE_CLASSES.help}>
                {dateSpec?.helperText ??
                  i18n._(msg`Data effettiva dell'incasso.`)}
              </p>
            )}
          </div>
        }
      />

      {/* (3b) Conto bancario — SELECT when accounts are known. */}
      <VendorPreviewField
        label={i18n._(msg`Conto`)}
        value={
          <div className="flex flex-col gap-1">
            {accountOptions.length > 0 ? (
              <StyledSelect
                className={ENVELOPE_CLASSES.input}
                value={accountValue}
                onChange={(e) => commit("payment_account_id", e.target.value)}
                disabled={disabled || accountSpec?.readonly}
                aria-label={i18n._(msg`Conto bancario`)}
              >
                <option value="">{i18n._(msg`— Predefinito azienda —`)}</option>
                {accountOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </StyledSelect>
            ) : (
              <input
                type="text"
                inputMode="numeric"
                className={ENVELOPE_CLASSES.input}
                value={accountValue}
                onChange={(e) => commit("payment_account_id", e.target.value)}
                disabled={disabled || accountSpec?.readonly}
                placeholder={i18n._(msg`ID conto (vuoto = predefinito)`)}
                aria-label={i18n._(msg`Conto bancario`)}
              />
            )}
            {errors?.payment_account_id ? (
              <p className={ENVELOPE_CLASSES.err}>
                {errors.payment_account_id.message}
              </p>
            ) : (
              <p className={ENVELOPE_CLASSES.help}>
                {accountSpec?.helperText ??
                  i18n._(
                    msg`Conto su cui è arrivato l'incasso (vuoto = predefinito azienda).`,
                  )}
              </p>
            )}
          </div>
        }
      />

      {/* (3c) Note. */}
      <VendorPreviewField
        label={i18n._(msg`Note`)}
        wide
        value={
          <div className="flex flex-col gap-1">
            <input
              type="text"
              className={ENVELOPE_CLASSES.input}
              value={notesValue}
              onChange={(e) => commit("notes", e.target.value)}
              disabled={disabled || notesSpec?.readonly}
              placeholder={i18n._(msg`Note interne (opzionale)`)}
              aria-label={i18n._(msg`Note sull'incasso`)}
            />
            {errors?.notes ? (
              <p className={ENVELOPE_CLASSES.err}>{errors.notes.message}</p>
            ) : notesSpec?.helperText ? (
              <p className={ENVELOPE_CLASSES.help}>{notesSpec.helperText}</p>
            ) : null}
          </div>
        }
      />
    </VendorPreviewShell>
  );
};
FicPaymentPreview.displayName = "FicPaymentPreview";
